/**
 * Single source of truth for referral source types used by:
 * - Find Referrals (search filter)
 * - Email Composer (template selection)
 * - generate-outreach-email edge function (prompt routing)
 *
 * The CANONICAL labels match the keys expected by the email-generation
 * edge function. The aliases let us migrate older records that were saved
 * with shorter labels (e.g. "Hospital", "Clinics").
 */

export const REFERRAL_SOURCE_TYPES = [
  "Hospital Discharge Planner",
  "Case Manager",
  "Home Health Agency",
  "Physician Office / Primary Care",
  "Physical / Occupational Therapist",
  "Clinic (Specialty / Urgent Care)",
  "Rehab Center",
  "Social Worker",
  "Assisted Living Facility",
  "Senior Living Facility",
  "Area Agency on Aging (AAA)",
  "Senior Legal Guardian",
  "Financial / Estate Planner",
  "Veterans Administration Program",
  "Former Client / Family",
  "Existing Client / Family",
] as const;

export type ReferralSourceType = (typeof REFERRAL_SOURCE_TYPES)[number];

/**
 * Maps legacy / shorthand labels (older Find Referrals UI) to the canonical
 * source type. Used both client-side (when displaying or sending an existing
 * record) and server-side (in the edge function) to keep behavior consistent.
 */
export const SOURCE_TYPE_ALIASES: Record<string, ReferralSourceType> = {
  "Hospital": "Hospital Discharge Planner",
  "Rehab Center": "Rehab Center",
  "Senior Living": "Senior Living Facility",
  "Physician Office": "Physician Office / Primary Care",
  "Social Worker": "Social Worker",
  "Home Health Agency": "Home Health Agency",
  "Senior Legal Guardian": "Senior Legal Guardian",
  "Physical/Occupational Therapists": "Physical / Occupational Therapist",
  "Financial/Estate Planners": "Financial / Estate Planner",
  "Area Agencies on Aging (AAA)": "Area Agency on Aging (AAA)",
  "Veterans Administration Programs": "Veterans Administration Program",
  "Clinics": "Clinic (Specialty / Urgent Care)",
  "Assisted Living": "Assisted Living Facility",
};

/** Returns the canonical source type for a given (possibly legacy) label. */
export function canonicalSourceType(raw: string | null | undefined): ReferralSourceType | null {
  if (!raw) return null;
  if ((REFERRAL_SOURCE_TYPES as readonly string[]).includes(raw)) {
    return raw as ReferralSourceType;
  }
  return SOURCE_TYPE_ALIASES[raw] ?? null;
}